import React from 'react';
import { FolderOpen, Trash2 } from 'lucide-react';
import { CollapsibleCard, CollapseControl } from './CollapsibleCard';
import { domainColorAlpha } from '../utils/constants';
import { MAX_SAVED_DIAGNOSES, type SavedDiagnosis } from '../utils/diagnosesStore';
import { cn } from '../../../utils/cn';

/**
 * Karta „Zapisane diagnozy" — lista wpisów z localStorage
 * (wiscv-saved-diagnoses-v1, świadomy wyjątek RODO — patrz diagnosesStore.ts).
 * Operacje na storze wykonuje SimpleWISCVSystem (callbacki); karta tylko
 * renderuje listę i potwierdza akcje destrukcyjne (confirm).
 */
interface SavedDiagnosesCardProps {
  diagnoses: SavedDiagnosis[];
  /** Id bieżącej diagnozy (edycje sekcji aktualizują ten wpis). */
  currentId: string | null;
  /** Blokuje „Wczytaj" podczas generacji/streamingu. */
  disabled?: boolean;
  onLoad: (id: string) => void;
  onDelete: (id: string) => void;
  onClearAll: () => void;
  collapse?: CollapseControl;
}

const formatSavedAt = (savedAt: number): string => {
  try {
    return new Date(savedAt).toLocaleString('pl-PL', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '';
  }
};

export const SavedDiagnosesCard: React.FC<SavedDiagnosesCardProps> = ({
  diagnoses,
  currentId,
  disabled = false,
  onLoad,
  onDelete,
  onClearAll,
  collapse,
}) => {
  return (
    <CollapsibleCard
      title="Zapisane diagnozy"
      icon={FolderOpen}
      open={collapse?.open}
      onOpenChange={collapse?.onOpenChange}
    >
      <div className="flex flex-col gap-3">
        {diagnoses.length === 0 ? (
          <p className="text-xs text-txt-tertiary">
            Brak zapisanych diagnoz — zapis następuje automatycznie po udanej generacji.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {diagnoses.map((entry) => {
              const fsiq = entry.compositeScores?.fsiq;
              const isCurrent = entry.id === currentId;
              const hasEdits = (entry.editedSections?.length ?? 0) > 0;
              return (
                <div
                  key={entry.id}
                  className={cn(
                    'p-3 rounded-xl border bg-surface-card flex flex-col gap-2',
                    isCurrent ? 'border-accent-border ring-1 ring-accent-border' : 'border-brd'
                  )}
                >
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    <span className="text-xs font-semibold text-txt-primary truncate">
                      {entry.patientInfo?.name?.trim() || 'Pacjent'}
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {fsiq && (
                        <span
                          className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full border"
                          style={{
                            color: 'var(--domain-fsiq-strong)',
                            borderColor: domainColorAlpha('var(--domain-fsiq)', 35),
                            backgroundColor: domainColorAlpha('var(--domain-fsiq)', 10),
                          }}
                        >
                          FSIQ {fsiq}
                        </span>
                      )}
                      {hasEdits && (
                        <span className="text-[9px] font-medium text-txt-tertiary border border-brd rounded-full px-1.5 py-0.5 bg-surface-subtle">
                          edytowano
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] text-txt-tertiary">
                      {formatSavedAt(entry.savedAt)}
                      {isCurrent && <span className="ml-1.5 text-accent-strong font-semibold">• bieżąca</span>}
                    </span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={() => onLoad(entry.id)}
                        disabled={disabled}
                        title="Wczytaj diagnozę do panelu wyniku"
                        className="px-2.5 py-1 bg-accent-soft border border-accent-border text-accent-strong hover:bg-accent-border rounded-lg text-[11px] font-semibold transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Wczytaj
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Usunąć zapisaną diagnozę (${entry.patientInfo?.name?.trim() || 'Pacjent'})?`)) {
                            onDelete(entry.id);
                          }
                        }}
                        title="Usuń zapisaną diagnozę"
                        aria-label="Usuń zapisaną diagnozę"
                        className="p-1.5 bg-surface-card border border-brd text-txt-tertiary hover:text-[var(--error)] hover:border-[var(--error)] rounded-lg transition-colors duration-200"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Stopka karty */}
        <div className="flex flex-col gap-2 pt-1 border-t border-brd">
          {/* Ostrzeżenie przy zbliżaniu się do limitu zapisów (I-5) — store
              przy przekroczeniu MAX_SAVED_DIAGNOSES ucina najstarszy wpis. */}
          {diagnoses.length >= MAX_SAVED_DIAGNOSES - 2 && (
            <p className="text-[11px] font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg px-2.5 py-1.5">
              Zbliżasz się do limitu {MAX_SAVED_DIAGNOSES} zapisów — przy
              przekroczeniu najstarszy wpis zostanie usunięty.
            </p>
          )}
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-txt-tertiary">
              Zapisy przechowywane lokalnie w tej przeglądarce
            </span>
            {diagnoses.length > 0 && (
              <button
                onClick={() => {
                  if (window.confirm('Wyczyścić wszystkie zapisane diagnozy? Tej operacji nie można cofnąć.')) {
                    onClearAll();
                  }
                }}
                className="px-2.5 py-1 bg-surface-card border border-brd text-txt-tertiary hover:text-[var(--error)] hover:border-[var(--error)] rounded-lg text-[11px] font-semibold transition-colors duration-200 shrink-0"
              >
                Wyczyść wszystkie
              </button>
            )}
          </div>
        </div>
      </div>
    </CollapsibleCard>
  );
};
